import React from 'react';
import type { Criterion, Phase, Threshold, DiaperModel } from '../../lib/types';
import type { BackendCriterion } from '../../lib/api-clip-monitor';
import type { AlarmEvent } from '../monitor/ClipTimeline';
import { useClipDataStore } from '../../stores/clipDataStore';

interface CriterionSelectorProps {
  currentCriterion: Criterion | null;
  currentPhase: Phase | null;
  currentDiaperModel: DiaperModel | null;
  availableCriteria: Array<{id: number; name: string}>;
  loadingCriteria: boolean;
  loadingCriterion: boolean;
  historicalCriterion: BackendCriterion | null;
  historicalCriterionInAvailable: boolean;
  criterionOverriddenByUser: boolean;
  setCriterionOverriddenByUser: (v: boolean) => void;
  handleCriterionChange: (criterionId: string) => void;
  handleRevertToHistoricalCriterion: () => void;
  changeNeededPercent: number | null;
  activeThreshold: Threshold | null;
  selectedDiaperAlarms: AlarmEvent[];
}

export const CriterionSelector: React.FC<CriterionSelectorProps> = ({
  currentCriterion,
  currentPhase,
  currentDiaperModel,
  availableCriteria,
  loadingCriteria,
  loadingCriterion,
  historicalCriterion,
  historicalCriterionInAvailable,
  criterionOverriddenByUser,
  setCriterionOverriddenByUser,
  handleCriterionChange,
  handleRevertToHistoricalCriterion,
  changeNeededPercent,
  activeThreshold,
  selectedDiaperAlarms,
}) => {
  return (
    <div className="md:w-1/2 space-y-3">
      {/* Criterion Selector */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <label className="block text-sm font-semibold text-gray-700">Criterion</label>
          {historicalCriterion && !criterionOverriddenByUser && (
            <span className="text-xs px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded font-medium">
              From backend (at diaper time)
            </span>
          )}
          {criterionOverriddenByUser && historicalCriterion && (
            <button
              type="button"
              onClick={handleRevertToHistoricalCriterion}
              className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded hover:bg-amber-200 font-medium"
            >
              ↩ Revert to historical
            </button>
          )}
        </div>
        <select
          value={currentCriterion?.id || ''}
          onChange={(e) => handleCriterionChange(e.target.value)}
          disabled={loadingCriteria || loadingCriterion || (!criterionOverriddenByUser && !!historicalCriterion && !historicalCriterionInAvailable)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm disabled:opacity-50"
        >
          <option value="">No criterion selected</option>
          {loadingCriteria && <option disabled>Loading criteria...</option>}
          {historicalCriterion && !historicalCriterionInAvailable && (
            <option value={historicalCriterion.criterion_id} disabled>
              [Historical] {historicalCriterion.name} (ID: {historicalCriterion.criterion_id}) — no longer available
            </option>
          )}
          {availableCriteria.map((criterion) => (
            <option key={criterion.id} value={criterion.id}>
              {criterion.name} (ID: {criterion.id})
            </option>
          ))}
        </select>
        {!criterionOverriddenByUser && historicalCriterion && !historicalCriterionInAvailable && (
          <p className="text-xs text-gray-500 mt-1">
            Historical criterion not in current backend.{' '}
            <button
              type="button"
              onClick={() => setCriterionOverriddenByUser(true)}
              className="text-indigo-600 underline hover:text-indigo-800"
            >
              Override with available criterion
            </button>
          </p>
        )}
      </div>

      {/* Phase Selector */}
      {currentCriterion && currentCriterion.content?.phases?.filter(p => p.name).length > 1 && (
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Phase</label>
          <select
            value={currentPhase?.id || ''}
            onChange={(e) => {
              const phaseId = parseInt(e.target.value, 10);
              const diaperModelCode = currentDiaperModel?.code || null;
              useClipDataStore.getState().selectPhase(phaseId, diaperModelCode);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
          >
            {currentCriterion.content?.phases?.filter(p => p.name).map((phase) => (
              <option key={phase.id} value={phase.id}>
                {phase.name} {phase.offMonitoring ? '(Off Monitoring)' : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Criterion-level settings */}
      {currentCriterion && <CriterionSettings criterion={currentCriterion} />}

      {/* Thresholds & Detection Settings */}
      <ThresholdDisplay
        currentPhase={currentPhase}
        currentCriterion={currentCriterion}
        currentDiaperModel={currentDiaperModel}
        changeNeededPercent={changeNeededPercent}
        activeThreshold={activeThreshold}
        selectedDiaperAlarms={selectedDiaperAlarms}
      />
    </div>
  );
};

const CriterionSettings: React.FC<{ criterion: Criterion }> = ({ criterion }) => {
  const c = criterion.content;
  const formatDuration = (sec: number) => {
    if (sec >= 3600) return `${(sec / 3600).toFixed(1).replace(/\.0$/, '')}h`;
    if (sec >= 60) return `${Math.round(sec / 60)}min`;
    return `${sec}s`;
  };
  const formatHour = (h: number) => {
    const hours = Math.floor(h);
    const mins = Math.round((h - hours) * 60);
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  };
  const phaseName = (phaseId: number) => {
    const p = c.phases?.find((ph: any) => ph.id === phaseId);
    return p?.name || `Phase ${phaseId}`;
  };

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
      <p className="text-blue-800 text-xs font-semibold">Criterion Settings:</p>

      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
        <span className="text-gray-600">Stabilization:</span>
        <span className="font-medium">{formatDuration(c.stabilizationPeriod_sec)}</span>
        {c.removePoolingPeriod_sec != null && (<><span className="text-gray-600">Remove Pooling:</span><span className="font-medium">{formatDuration(c.removePoolingPeriod_sec)}</span></>)}
        {c.stagnationPeriod_sec != null && (<><span className="text-gray-600">Stagnation Period:</span><span className="font-medium">{formatDuration(c.stagnationPeriod_sec)}</span></>)}
        {c.stagnantWetPeriod_sec != null && (<><span className="text-gray-600">Stagnant Wet Period:</span><span className="font-medium">{formatDuration(c.stagnantWetPeriod_sec)}</span></>)}
        {c.clipLossTimeout_sec != null && (<><span className="text-gray-600">Clip Loss Timeout:</span><span className="font-medium">{formatDuration(c.clipLossTimeout_sec)}</span></>)}
        {c.diaperTooOldPeriod_hr != null && (<><span className="text-gray-600">Diaper Too Old:</span><span className="font-medium">{c.diaperTooOldPeriod_hr}h</span></>)}
        {c.shortDetectionEnabled != null && (<><span className="text-gray-600">Short Detection:</span><span className={`font-medium ${c.shortDetectionEnabled ? 'text-green-700' : 'text-gray-400'}`}>{c.shortDetectionEnabled ? 'Enabled' : 'Disabled'}</span></>)}
      </div>

      {c.schedule && (
        <div className="pt-1 border-t border-blue-200">
          <p className="text-blue-700 text-xs font-semibold mb-0.5">Schedule:</p>
          <div className="text-xs space-y-0.5">
            <div className="text-gray-600">Timezone: <span className="font-medium text-gray-800">{c.schedule.timeZone}</span></div>
            {c.schedule.default && (
              <div className="text-gray-600">Default: <span className="font-medium text-gray-800">{phaseName(c.schedule.default.phaseId)}</span></div>
            )}
            {c.schedule.daily && c.schedule.daily.length > 0 && (
              <div>
                {c.schedule.daily.map((slot: any, i: number) => (
                  <div key={i} className="text-gray-600">{formatHour(slot.startHour)} – {formatHour(slot.endHour)}: <span className="font-medium text-gray-800">{phaseName(slot.phaseId)}</span></div>
                ))}
              </div>
            )}
            {c.schedule.weekly && c.schedule.weekly.length > 0 && (
              <div className="text-gray-600">Weekly overrides: {c.schedule.weekly.length}</div>
            )}
          </div>
        </div>
      )}

      {(c.description || c.startTime || c.active != null) && (
        <div className="pt-1 border-t border-blue-200 text-xs text-gray-500 space-y-0.5">
          {c.description && <div>{c.description}</div>}
          <div className="flex gap-3">
            {c.active != null && (<span className={c.active ? 'text-green-600' : 'text-red-500'}>{c.active ? 'Active' : 'Inactive'}</span>)}
            {c.defaultPolicy && <span>Default Policy</span>}
            {c.startTime && <span>Since {new Date(c.startTime).toLocaleDateString()}</span>}
          </div>
        </div>
      )}

      <details className="pt-1 border-t border-blue-200">
        <summary className="text-blue-600 text-xs cursor-pointer">Raw criterion fields</summary>
        <pre className="text-xs text-gray-600 mt-1 whitespace-pre-wrap break-all max-h-40 overflow-y-auto">
          {JSON.stringify(Object.fromEntries(Object.entries(c).filter(([k]) => k !== 'phases')), null, 2)}
        </pre>
      </details>
    </div>
  );
};

interface ThresholdDisplayProps {
  currentPhase: Phase | null;
  currentCriterion: Criterion | null;
  currentDiaperModel: DiaperModel | null;
  changeNeededPercent: number | null;
  activeThreshold: Threshold | null;
  selectedDiaperAlarms: AlarmEvent[];
}

const ThresholdDisplay: React.FC<ThresholdDisplayProps> = ({
  currentPhase,
  currentCriterion,
  currentDiaperModel,
  changeNeededPercent,
  activeThreshold,
  selectedDiaperAlarms,
}) => {
  if (!currentCriterion) return null;

  if (!currentPhase || changeNeededPercent === null) {
    return (
      <div className="bg-gray-100 border border-gray-300 rounded-lg p-3">
        <p className="text-gray-600 text-xs">
          {currentPhase?.offMonitoring ? 'Monitoring OFF' : 'No phase selected'}
        </p>
      </div>
    );
  }

  const realAlarms = selectedDiaperAlarms.filter(a => !a.hypothetical);
  const hypoAlarms = selectedDiaperAlarms.filter(a => a.hypothetical);

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 space-y-2">
      <div>
        <p className="text-yellow-800 text-sm font-semibold">
          Change Needed: {changeNeededPercent}%
        </p>
        {currentDiaperModel && (
          <p className="text-yellow-700 text-xs">
            = {Math.round(currentDiaperModel.capacity * (changeNeededPercent / 100))}mL of {currentDiaperModel.capacity}mL capacity
          </p>
        )}
      </div>

      {activeThreshold && (
        <div className="pt-2 border-t border-yellow-300">
          <p className="text-blue-800 text-sm font-semibold mb-1">Leak Detection Modes:</p>
          <div className="grid grid-cols-2 gap-1 text-xs">
            <div className={activeThreshold.leakClipStatusEnabled ? 'text-green-700' : 'text-gray-500'}>
              {activeThreshold.leakClipStatusEnabled ? '✓' : '✗'} Clip Status
            </div>
            <div className={activeThreshold.leakAdcValuesEnabled ? 'text-green-700' : 'text-gray-500'}>
              {activeThreshold.leakAdcValuesEnabled ? '✓' : '✗'} ADC Values
            </div>
            <div className={activeThreshold.leakLevelEnabled ? 'text-green-700' : 'text-gray-500'}>
              {activeThreshold.leakLevelEnabled ? '✓' : '✗'} Wetness Level
            </div>
            <div className={activeThreshold.leakUnknownStatusEnabled ? 'text-green-700' : 'text-gray-500'}>
              {activeThreshold.leakUnknownStatusEnabled ? '✓' : '✗'} When Pooling status
            </div>
          </div>
        </div>
      )}

      {currentPhase.offMonitoring && (
        <div className="pt-2 border-t border-orange-300">
          <p className="text-orange-700 text-xs font-semibold">⚠️ Monitoring is OFF for this phase</p>
        </div>
      )}

      {realAlarms.length > 0 && (
        <div className="pt-2 border-t border-red-300">
          <p className="text-red-800 text-sm font-semibold mb-1">
            Alarm Events: {realAlarms.length}
          </p>
          <div className="text-xs space-y-0.5">
            <p className="text-red-700">
              First: {realAlarms[0].timestamp.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              {' — '}
              Last: {realAlarms[realAlarms.length - 1].timestamp.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </p>
            {(() => {
              const breakdown: Record<string, number> = {};
              for (const a of realAlarms) breakdown[a.reason] = (breakdown[a.reason] || 0) + 1;
              return Object.entries(breakdown).map(([reason, count]) => (
                <p key={reason} className="text-red-600">• {reason}: {count}x</p>
              ));
            })()}
          </div>
        </div>
      )}
      {hypoAlarms.length > 0 && (
        <div className="pt-2 border-t border-amber-300">
          <p className="text-amber-700 text-sm font-semibold mb-1">
            Would-trigger (if enabled): {hypoAlarms.length}
          </p>
          <div className="text-xs space-y-0.5">
            {(() => {
              const breakdown: Record<string, number> = {};
              for (const a of hypoAlarms) breakdown[a.reason] = (breakdown[a.reason] || 0) + 1;
              return Object.entries(breakdown).map(([reason, count]) => (
                <p key={reason} className="text-amber-600">• {reason}: {count}x (not enabled in criterion)</p>
              ));
            })()}
          </div>
        </div>
      )}
      {realAlarms.length === 0 && hypoAlarms.length === 0 && activeThreshold && (
        <div className="pt-2 border-t border-green-300">
          <p className="text-green-700 text-xs font-semibold">No alarm events detected for this diaper</p>
        </div>
      )}
      {(realAlarms.length > 0 || hypoAlarms.length > 0) && currentCriterion?.content.stabilizationPeriod_sec ? (
        <p className="text-gray-500 text-xs mt-1">(stabilization: {currentCriterion.content.stabilizationPeriod_sec}s)</p>
      ) : null}
    </div>
  );
};
